import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Select,
  Table,
  Space,
  Popconfirm,
  message,
  Modal,
  Typography,
  Spin,
  Pagination,
  Divider,
  Checkbox
} from "antd";
import axios from "axios";   // ✅ for API calls
import SidebarLayout from "../BankerUI/SidebarLayout";
import _BankAdminService from "../../modules/BankAdminService";
import _BankerService from "../../modules/BankerService";
import { COMMON_ERROR_MSG } from "../../constants/constants";
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined, ReloadOutlined, MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import CurrencyInput from "../../components/CurrencyInput";
import _commonService from "../../modules/commonService";
import AlphabetInput from "../../components/AlphabetInput";
import FormItemInput from "../../components/FormItems/FormItemInput";
import FormItemAlphabetInput from "../../components/FormItems/FormItemAlphabetInput";
import FormItemPhoneNumber from "../../components/FormItems/FormItemPhoneNumber";
import FormItemSelect from "../../components/FormItems/FormItemSelect";
import FormItemCurrencyInput from "../../components/FormItems/FormItemCurrencyInput";
import ButtonComponent from "../../components/Button/Button";
import CustomModal from "../../components/Modal/Modal";
import "./BankAdminProfile.scss";
const { Option } = Select;
const { Title, Text } = Typography;
const { confirm } = Modal;

const HIDE_RECOMMENDATION_ROLE_NAMES = ["Loan Officer", "Wealth Advisor", "Compliance Administrator"];
const HIDE_APPROVAL_ROLE_NAMES = ["Senior Private Banker", "Underwriter Banker", "Wealth Advisor", "Compliance Administrator"];

const BankAdminDashboard = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editBankerUserForm] = Form.useForm();
  const selectedEditRole = Form.useWatch("role", editForm);
  const bankerRolesWatch = Form.useWatch("bankerRoles", form);
  const editBankerRolesWatch = Form.useWatch("bankerRoles", editBankerUserForm);

  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCreateUserModalVisible, setIsCreateUserModalVisible] = useState(false);
  const [editingBankerUser, setEditingBankerUser] = useState(null);
  const [isEditBankerUserModalVisible, setIsEditBankerUserModalVisible] = useState(false);
  // Role rows removed while editing -- sent back as IsDeleted:true instead of just being
  // dropped from the payload, so the backend soft-deletes them (BankerRoles.IsDeleted) rather
  // than inferring deletion from absence.
  const [removedEditBankerRoleIds, setRemovedEditBankerRoleIds] = useState([]);
  const [rollTypes, setRollTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const bankadminIdRaw = localStorage.getItem("BankAdminId");
  const BankAdminFirstName = localStorage.getItem("BankAdminFirstName");
  const BankAdminID = bankadminIdRaw ? parseInt(bankadminIdRaw) : null;
  const [totalCount, setTotalCount] = useState(0);

  const selectedEditRoleName = rollTypes.find((obj) => obj.Id === selectedEditRole)?.Name;
  const shouldHideRecommendationLimitEdit = HIDE_RECOMMENDATION_ROLE_NAMES.includes(selectedEditRoleName);
  const shouldHideApprovalLimitEdit = HIDE_APPROVAL_ROLE_NAMES.includes(selectedEditRoleName);

  useEffect(() => {
    getAllBankAdminRollType();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (shouldHideRecommendationLimitEdit) {
      editForm.setFieldsValue({ recommendationLimit: undefined });
    }
    if (shouldHideApprovalLimitEdit) {
      editForm.setFieldsValue({ approvalLimit: undefined });
    }
  }, [editForm, shouldHideRecommendationLimitEdit, shouldHideApprovalLimitEdit]);

  useEffect(() => {
    fetchUsers(pageIndex, pageSize);
  }, [pageIndex, pageSize]);
  const getAllBankAdminRollType = async () => {
    try {
      setLoading(true);
      const res = await _BankAdminService.GetAllRollByParameter("admin");
      if (res.data.Obj) {
        setRollTypes(res.data.Obj);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Create User -- one Banker record with one or more roles in BankerRoles
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const midadleFirstLetter = values.middleName || "";
      const bankerRoles = (values.bankerRoles || []).map((r) => {
        const roleName = rollTypes.find((obj) => obj.Id === r.role)?.Name;
        const hideRec = HIDE_RECOMMENDATION_ROLE_NAMES.includes(roleName);
        const hideApp = HIDE_APPROVAL_ROLE_NAMES.includes(roleName);
        return {
          roleId: r.role,
          recommendation_Limit: hideRec ? undefined : r.recommendationLimit,
          approval_Limit: hideApp ? undefined : r.approvalLimit,
        };
      });
      const payload = {
        id: 0,
        email: values.email?.trim(),
        firstName: values.firstName,
        middleInitial: midadleFirstLetter,
        lastName: values.lastName,
        contactNumber: values.contactNumber || "",
        bankName: values.bankName || "",
        officeAddress: values.officeAddress || "",
        city: values.city || "",
        state: values.state || "",
        zipCode: values.zipCode || "",
        createdBy: BankAdminID,
        bankAdminId: BankAdminID,
        bankerRoles,
        IsSSOLogin: values.isSSOLogin === true,
      };

      const res = await _BankerService.addUpdateBankerUserData(payload);

      if (res.data?.IsSuccess) {
        setLoading(false);
        fetchUsers(pageIndex, pageSize);
        message.success(res.data.Message || "User created successfully");
        fetchUsers();
        form.resetFields();
        setIsCreateUserModalVisible(false)
      } else {
        setLoading(false);
        message.error(res.data?.Message || "Something went wrong");
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      message.error("Error creating user");
    }
  };
  const fetchUsers = async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const params = {
        BankAdminId: BankAdminID,
        pageIndex: page,
        pageSize: size,
        searchTerm: "",
        sortBy: "id",
        sortAsc: false,
        startDate: "",
        endDate: "",
      };
      const res = await _BankerService.getAllInstitutionUsersByBankAdminId(params);

      if (res.data?.IsSuccess) {
        setLoading(false);
        setUsers(res.data.Obj.List);
        setTotalCount(res.data.Obj.TotalRecords || 0);

      } else {
        setLoading(false);
        message.error("Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Error fetching users");
    }
    setLoading(false);
  };


  // ✅ Delete user
  const handleDelete = (key) => {
    confirm({
      title: "Are you sure you want to delete this user?",
      content: "This action cannot be undone.",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          const res = await _BankAdminService.deleteUserID(key);
          if (res.data.IsSuccess) {
            message.success(res.data.Message);
            fetchUsers(pageIndex, pageSize);
          } else {
            message.error(res.data?.Message);
          }
        } catch (err) {
          console.error(err);
          message.error("Something went wrong");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // ✅ Edit modal open
  const handleEdit = (record) => {
    setEditingUser(record);
    editForm.setFieldsValue({
      firstName: record.FirstName,
      middleName: record.MiddleInitial,
      lastName: record.LastName,
      email: record.Email?.trim(),
      role: record.RoleId,
      recommendationLimit: record.Recommendation_Limit,
      approvalLimit: record.Approval_Limit,
    });
    setIsEditModalVisible(true);
  };

  // Save updated user via API
  const handleUpdate = async () => {
    setLoading(true);
    try {
      const values = await editForm.validateFields();
      const midadleFirstLetter = values.middleName?.charAt(0).toUpperCase() || "";
      const payload = {
        id: editingUser.Id || 0,
        email: values.email,
        firstName: values.firstName,
        middleInitial: midadleFirstLetter,
        lastName: values.lastName,
        roleId: values.role,
        updatedBy: "",
        updatedDate: new Date().toISOString(),
        recommendation_Limit: shouldHideRecommendationLimitEdit ? undefined : values.recommendationLimit,
        approval_Limit: shouldHideApprovalLimitEdit ? undefined : values.approvalLimit,
        BankAdminId: BankAdminID
      };

      const res = await _BankAdminService.UpdateUserManagementData(payload);
      if (res.data.IsSuccess) {
        setLoading(false);
        message.success(res.data.Message);
        setIsEditModalVisible(false);
        setEditingUser(null);
        fetchUsers(pageIndex, pageSize);
      } else {
        setLoading(false);
        message.error(res.data?.Message || "Update failed");
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      message.error("Error updating user");
    }
  };
  const handleResendEmailOTP = (record) => {
    const { Email, RoleId } = record

    confirm({
      title: <span>Are you sure you want to resend the OTP to this email <Text className='f16' strong>{Email ? `(${Email})` : ""}</Text>?</span>,
      icon: <ExclamationCircleOutlined />,
      width: 500,
      okText: <span id="yesBtn">Yes</span>,
      cancelText: <span id="noBtn">No</span>,
      okType: "primary",
      async onOk() {
        try {
          setLoading(true);
          const res = await _BankAdminService.ReSendEmailWithLinkAndPasscode(Email, RoleId);
          if (res.data.IsSuccess) {
            message.success(res.data.Message || 'OTP sent successfully');
          } else {
            message.error(res.data.Message || COMMON_ERROR_MSG);
            setLoading(false);
          }
        } catch (error) {
          console.log(error);
          setLoading(false);
        } finally {
          setLoading(false);
        }

      }
    });
  };
  // ✅ Edit modal open -- multi-role Banker + BankerRoles user
  const handleEditBankerUser = (record) => {
    setEditingBankerUser(record);
    setRemovedEditBankerRoleIds([]);
    editBankerUserForm.setFieldsValue({
      firstName: record.FirstName,
      middleName: record.MiddleInitial,
      lastName: record.LastName,
      email: record.Email?.trim(),
      bankerRoles: (record.BankerRoles || []).map((r) => ({
        role: r.RoleId,
        recommendationLimit: r.Recommendation_Limit,
        approvalLimit: r.Approval_Limit,
      })),
    });
    setIsEditBankerUserModalVisible(true);
  };

  // Save updated multi-role Banker user via the same API used to create one
  const onFinishEditBankerUser = async (values) => {
    setLoading(true);
    try {
      const midadleFirstLetter = values.middleName?.charAt(0).toUpperCase() || "";
      const bankerRoles = (values.bankerRoles || []).map((r) => {
        const roleName = rollTypes.find((obj) => obj.Id === r.role)?.Name;
        const hideRec = HIDE_RECOMMENDATION_ROLE_NAMES.includes(roleName);
        const hideApp = HIDE_APPROVAL_ROLE_NAMES.includes(roleName);
        return {
          roleId: r.role,
          recommendation_Limit: hideRec ? undefined : r.recommendationLimit,
          approval_Limit: hideApp ? undefined : r.approvalLimit,
          isDeleted: false,
        };
      });
      // Roles removed via the minus icon during this edit session -- sent as explicit
      // IsDeleted:true entries so the backend soft-deletes them instead of assuming deletion
      // just because they're missing from the submitted list.
      const removedBankerRoles = removedEditBankerRoleIds.map((roleId) => ({
        roleId,
        isDeleted: true,
      }));
      const payload = {
        id: editingBankerUser.Id,
        email: values.email?.trim(),
        firstName: values.firstName,
        middleInitial: midadleFirstLetter,
        lastName: values.lastName,
        contactNumber: editingBankerUser.ContactNumber || "",
        bankName: editingBankerUser.BankName || "",
        officeAddress: editingBankerUser.OfficeAddress || "",
        city: editingBankerUser.City || "",
        state: editingBankerUser.State || "",
        zipCode: editingBankerUser.ZipCode || "",
        updatedBy: BankAdminID,
        bankAdminId: BankAdminID,
        bankerRoles: [...bankerRoles, ...removedBankerRoles],
      };

      const res = await _BankerService.addUpdateBankerUserData(payload);
      if (res.data?.IsSuccess) {
        message.success(res.data.Message || "User updated successfully");
        setIsEditBankerUserModalVisible(false);
        setEditingBankerUser(null);
        setRemovedEditBankerRoleIds([]);
        fetchUsers(pageIndex, pageSize);
      } else {
        message.error(res.data?.Message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      message.error("Error updating user");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtpForBankerUser = (record) => {
    const { Email } = record;

    confirm({
      title: <span>Are you sure you want to resend the OTP to this email <Text className='f16' strong>{Email ? `(${Email})` : ""}</Text>?</span>,
      icon: <ExclamationCircleOutlined />,
      width: 500,
      okText: <span id="yesBtn">Yes</span>,
      cancelText: <span id="noBtn">No</span>,
      okType: "primary",
      async onOk() {
        try {
          setLoading(true);
          const res = await _BankerService.reSendEmailWithLinkAndPasscodeForBankerUser(Email);
          if (res.data.IsSuccess) {
            message.success(res.data.Message || 'OTP sent successfully');
          } else {
            message.error(res.data.Message || COMMON_ERROR_MSG);
          }
        } catch (error) {
          console.log(error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // ✅ Delete multi-role Banker user -- blocked if assigned to a Sponser (SponserUsers.UserId)
  const handleDeleteBankerUser = (record) => {
    confirm({
      title: "Are you sure you want to delete this user?",
      content: "This action cannot be undone.",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          const res = await _BankerService.deleteBankerUserById(record.Id);
          if (res.data?.IsSuccess) {
            message.success(res.data.Message);
            fetchUsers(pageIndex, pageSize);
          } else {
            message.error(res.data?.Message || "Something went wrong");
          }
        } catch (err) {
          console.error(err);
          message.error("Something went wrong");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "FirstName",
      key: "name",
      align: "left",
      render: (text, record) =>
        `${record.FirstName} ${record.MiddleInitial || ""} ${record.LastName}`,
    },
    {
      title: "Email",
      dataIndex: "Email",
      key: "Email",
      align: "left",
    },
    // {
    //   title: "Role",
    //   dataIndex: "Role",
    //   key: "role",

    // },
    {
      title: "Role",
      key: "role",
      align: "left",
      render: (_, record) => {
        if (record.Source === "BankerRoles") {
          return (record.BankerRoles || []).map((r) => (
            <span className="role-badge" key={r.RoleId} style={{ marginRight: 6 }}>
              {rollTypes.find((obj) => obj.Id === r.RoleId)?.Name || r.RoleId}
            </span>
          ));
        }
        return (
          <span className="role-badge">
            {record.Role}
          </span>
        );
      },
    },
    {
      title: "Limits",
      key: "limits",
      align: "left",
      render: (_, record) => {
        if (record.Source === "BankerRoles") {
          const rows = (record.BankerRoles || []).map((r) => {
            const roleName = rollTypes.find((obj) => obj.Id === r.RoleId)?.Name;
            const hideRec = HIDE_RECOMMENDATION_ROLE_NAMES.includes(roleName);
            const hideApp = HIDE_APPROVAL_ROLE_NAMES.includes(roleName);
            if (hideRec && hideApp) return null;
            return (
              <div key={r.RoleId}>
                {!hideRec && <>{roleName} Rec: ${_commonService.formatNumberWithCommas(r.Recommendation_Limit)}</>}
                {!hideApp && <>{roleName} App: ${_commonService.formatNumberWithCommas(r.Approval_Limit)}</>}
              </div>
            );
          }).filter(Boolean);
          return rows.length ? rows : <span>N/A</span>;
        }
        const hideRec = HIDE_RECOMMENDATION_ROLE_NAMES.includes(record.Role);
        const hideApp = HIDE_APPROVAL_ROLE_NAMES.includes(record.Role);
        if (hideRec && hideApp) return <span>N/A</span>;
        return (
          <span>
            {!hideRec && <>Rec: ${_commonService.formatNumberWithCommas(record.Recommendation_Limit)}<br /></>}
            {!hideApp && <>App: ${_commonService.formatNumberWithCommas(record.Approval_Limit)}</>}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "left",
      render: (_, record) => {
        if (record.Source === "BankerRoles") {
          return (
            <Space>
              <Button type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditBankerUser(record)}>
                Edit
              </Button>
              <Button danger
                className="link-danger"
                icon={<DeleteOutlined />}
                type="link" onClick={() => handleDeleteBankerUser(record)}>
                Delete
              </Button>

              {!record.IsRegistered &&
                (<Button type="link" icon={<ReloadOutlined />} onClick={() => handleResendOtpForBankerUser(record)}>
                  Resend OTP
                </Button>)}
            </Space>
          );
        }
        return (
          <Space>
            <Button type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}>
              Edit
            </Button>
            <Button danger
              className="link-danger"
              icon={<DeleteOutlined />}
              type="link" onClick={() => handleDelete(record.Id)}>
              Delete
            </Button>

            {!record.IsRegistered &&
              (<Button type="link" icon={<ReloadOutlined />} onClick={() => handleResendEmailOTP(record)}>
                Resend  OTP
              </Button>)}
          </Space>
        );
      },
    },
  ];
  const handleCreateUser = () => {
    form.resetFields();
    const loanOfficerId = rollTypes.find((obj) => obj.Name === "Loan Officer")?.Id;
    form.setFieldsValue({ bankerRoles: [{ role: loanOfficerId }] });
    setIsCreateUserModalVisible(true);
  };
  return (
    <Spin spinning={loading}>
      <SidebarLayout>
        <div className="bank-admin-dashboard" style={{ margin: "0 auto", padding: 20 }}>
          {/* <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ marginBottom: 0 }}>
                Hello, {BankAdminFirstName}!
              </Title>
            </Col>

            <Col>
              <Row gutter={12} align="middle">
                <Col>
                 <Form form={form}>
                    <Form.Item
                      name="role"
                      rules={[
                        { required: true, message: "Role is required" },
                      ]}
                      style={{ marginBottom: 0 }}
                      
                    >
                      <Select placeholder="Select role" style={{ minWidth: 250 }}>
                        {rollTypes.map((obj) => (
                          <Option key={obj.Id} value={obj.Id}>
                            {obj.Name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Form>
                </Col>
                <Col>
                  <Button type="primary" onClick={handleCreateUser}>
                    Create User
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row> */}

          <Row justify="space-between" align="middle">
            <Col>
              <Title level={3} style={{ marginBottom: 0 }}>
                Hello, {BankAdminFirstName}!
              </Title>
            </Col>

            <Col>
              <ButtonComponent type="primary" className="createUserButton" onClick={handleCreateUser} text="Create User" />
            </Col>
          </Row>


          <Divider></Divider>

          {/* Table */}
          <Table
            rowKey={(record) => `${record.Source}-${record.Id}`}
            columns={columns}
            dataSource={users}
            pagination={false}
            style={{ marginTop: "30px" }}
            className="transparent-table"
          // scroll={{ y: 400 }}
          />
          <Pagination
            className="pagination"
            showSizeChanger
            current={pageIndex}
            pageSize={pageSize}
            total={totalCount}
            onChange={(page, size) => {
              setPageIndex(page);
              setPageSize(size);
            }}
            style={{ marginTop: 16, textAlign: "right" }}
          />

          {/* Edit Modal */}
          <CustomModal
            visible={isEditModalVisible}
            onClose={() => setIsEditModalVisible(false)}
            onPrimaryAction={handleUpdate}
            onSecondaryAction={() => setIsEditModalVisible(false)}
            title="Edit User"
            primaryButtonText="Update"
            secondaryButtonText="Cancel"
            showSecondaryButton
            className="edit-user-popoup"
            primaryButtonProps={{
              disabled: editingUser?.IsRegistered === true,
            }}
          >
            {/* <Modal
            title={<Typography.Title style={{ marginBottom: 0 }} level={4}>Edit User</Typography.Title>}
            open={isEditModalVisible}
            onOk={handleUpdate}
            onCancel={() => setIsEditModalVisible(false)}
            okText="Update"
            className="edit-user-popoup"
            okButtonProps={{ disabled: editingUser?.IsRegistered === true }}
          > */}
            <Form form={editForm} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <FormItemInput
                    label="First Name"
                    name="firstName"
                    placeholder="Enter First Name"
                    rules={[
                      { required: true, message: "First Name is required" },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  {/* <Form.Item name="firstName" label="First Name" rules={[
                {
                  required: true,
                  message: 'First Name is required',
                },
              ]} className="w50">
                <Input placeholder="Enter First Name" />
              </Form.Item> */}
                  <FormItemAlphabetInput
                    label="Middle Name"
                    name="middleName"
                    placeholder="Enter Middle Name"
                    // rules={[
                    //   { required: false, max: 30, message: "Only 30 characters allowed" },
                    // ]}
                    maxLength={30}
                  />
                  
                </Col>

                <Col span={12}>
                  <FormItemInput
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter Last Name"
                    rules={[
                      { required: true, message: "Last Name is required" },
                    ]}
                  />
                  {/* <Form.Item name="lastName" label="Last Name" rules={[
                    {
                      required: true,
                      message: 'Last Name is required',
                    },
                  ]} className="w50">
                    <Input placeholder="Enter Last Name" />
                  </Form.Item> */}
                </Col>
                <Col span={12}>
                  <FormItemInput
                    label="Email"
                    name="email"
                    placeholder="Enter Email"
                    rules={[
                      { required: true, message: "Email is required" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  />
                  {/* <Form.Item name="email" label="Email"
                rules={[
                  {
                    required: true,
                  },
                  {
                    type: "email",
                    message: "Please enter a valid email",
                  },
                ]} className="w50">
                <Input placeholder="Enter Email" />
              </Form.Item>                      */}
                </Col>

                <Col span={12}>
                  <FormItemSelect
                    label="Role"
                    name="role"
                    placeholder="Select role"
                    rules={[
                      { required: true, message: "Role is required" },
                    ]}
                    options={rollTypes.map((obj) => ({
                      value: obj.Id,
                      label: obj.Name,
                    }))}
                  />
                  {/* <Form.Item name="role" label="Role" rules={[
                    {
                      required: true,
                      message: 'Role is required',
                    },
                  ]} className="w50">
                    <Select disabled={editingUser?.IsRegistered === true}>
                      {rollTypes.map((obj) => (
                        <Option key={obj.Id} value={obj.Id}>
                          {obj.Name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item> */}
                </Col>
                {!shouldHideRecommendationLimitEdit && (
                  <Col span={12}>
                    <FormItemCurrencyInput
                      label="Recommendation Limit $"
                      name="recommendationLimit"
                      placeholder="Enter Recommendation Limit"
                      rules={[
                        {
                          required: true,
                          message: 'Recommendation Limit is required',
                        },
                      ]}
                    />
                  </Col>
                )}
                {!shouldHideApprovalLimitEdit && (
                  <Col span={12}>
                    <FormItemCurrencyInput
                      label="Approval Limit $"
                      name="approvalLimit"
                      placeholder="Enter Approval Limit"
                      rules={[
                        {
                          required: true,
                          message: 'Approval Limit is required',
                        },
                      ]}
                    />
                  </Col>
                )}
              </Row>
            </Form>
            {/* </Modal> */}
          </CustomModal>
          <CustomModal
            visible={isCreateUserModalVisible}
            onClose={() => setIsCreateUserModalVisible(false)}
            title="Create User"
            showPrimaryButton={false}
            className="edit-user-popoup"
            primaryButtonText=""
            secondaryButtonText=""
          >
            {/* <Modal
            title={<Typography.Title style={{ marginBottom: 0 }} level={4}>Create User</Typography.Title>}
            open={isCreateUserModalVisible}
            footer={null}
            onCancel={() => setIsCreateUserModalVisible(false)}
            className="edit-user-popoup"
          > */}
            {/* Create Form */}
            <Form form={form} layout="vertical" onFinish={onFinish} className="bank_admin_dashboard">
              <Row gutter={16}>
                <Col span={12}>
                  <FormItemInput
                    label="First Name"
                    name="firstName"
                    placeholder="Enter First Name"
                    rules={[
                      { required: true, message: "First Name is required" },
                    ]}
                  />
                  {/* <Form.Item name="firstName" label="First Name"
                    rules={[
                      {
                        required: true,
                        message: 'First Name is required',
                      },
                    ]}
                  >
                    <Input placeholder="Enter First Name" />
                  </Form.Item> */}
                </Col>
                <Col span={12}>
                  <FormItemAlphabetInput
                    label="Middle Name"
                    name="middleName"
                    placeholder="Enter Middle Name"
                    // rules={[
                    //   { required: false, max: 1, message: "Only one character allowed" },
                    // ]}
                    maxLength={30}
                  />
                  {/* <Form.Item name="middleName" label="Middle Initial"
                    rules={[
                      { required: false, max: 1, message: "Only one character allowed" },
                    ]}
                  >
                    <AlphabetInput placeholder="Enter Middle Initial" maxLength={1} />
                  </Form.Item> */}
                </Col>
                <Col span={12}>
                  <FormItemInput
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter Last Name"
                    rules={[
                      { required: true, message: "Last Name is required" },
                    ]}
                  />


                  {/* <Form.Item name="lastName" label="Last Name" rules={[
                    {
                      required: true,
                      message: 'Last Name is required',
                    },
                  ]}>
                    <Input placeholder="Enter Last Name" />
                  </Form.Item> */}
                </Col>
                <Col span={12}>
                  <FormItemInput
                    label="Email"
                    name="email"
                    placeholder="Enter Email Name"
                    rules={[
                      { required: true, message: "Email is required" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  />


                  {/* <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      {
                        required: true,
                        message: 'Email is required',
                      },
                    ]}
                  >
                    <Input placeholder="Enter Email Name" />
                  </Form.Item> */}
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <FormItemPhoneNumber
                    label="Contact Number"
                    name="contactNumber"
                    form={form}
                    placeholder="Enter Contact Number"
                    rules={[
                      { required: true, message: "Contact Number is required" },
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.resolve();
                          }
                          const digits = value.replace(/\D/g, '');
                          if (digits.length === 10) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Wrong format! please use this format (XXX) XXX-XXXX")
                          );
                        },
                      }
                    ]}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="isSSOLogin" valuePropName="checked" initialValue={false} hidden>
                    <Checkbox>SSO Login</Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Form.List name="bankerRoles">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => {
                      const isLast = index === fields.length - 1;
                      const selectedRoleId = bankerRolesWatch?.[index]?.role;
                      const selectedRoleName = rollTypes.find((obj) => obj.Id === selectedRoleId)?.Name;
                      // No role picked yet on this row -> show neither limit field until one is chosen.
                      const hideRec = !selectedRoleId || HIDE_RECOMMENDATION_ROLE_NAMES.includes(selectedRoleName);
                      const hideApp = !selectedRoleId || HIDE_APPROVAL_ROLE_NAMES.includes(selectedRoleName);
                      const showLimit = !hideRec || !hideApp;
                      const takenElsewhere = (bankerRolesWatch || [])
                        .filter((_, i) => i !== index)
                        .map((r) => r?.role);
                      const roleOptions = rollTypes
                        .filter((obj) => obj.Id === selectedRoleId || !takenElsewhere.includes(obj.Id))
                        .map((obj) => ({ value: obj.Id, label: obj.Name }));

                      return (
                        <Row gutter={16} key={field.key} align="top">
                          <Col span={showLimit ? 8 : 16}>
                            <FormItemSelect
                              label="Role"
                              name={[field.name, "role"]}
                              placeholder="Select role"
                              rules={[{ required: true, message: "Role is required" }]}
                              options={roleOptions}
                            />
                          </Col>
                          {!hideRec && (
                            <Col span={8}>
                              <FormItemCurrencyInput
                                label="Recommendation Limit $"
                                name={[field.name, "recommendationLimit"]}
                                placeholder="Enter Recommendation Limit"
                                rules={[
                                  { required: true, message: "Recommendation Limit is required" },
                                ]}
                              />
                            </Col>
                          )}
                          {!hideApp && (
                            <Col span={8}>
                              <FormItemCurrencyInput
                                label="Approval Limit $"
                                name={[field.name, "approvalLimit"]}
                                placeholder="Enter Approval Limit"
                                rules={[
                                  { required: true, message: "Approval Limit is required" },
                                ]}
                              />
                            </Col>
                          )}
                          <Col span={8} style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 33 }}>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                className="removeRoleIcon"
                                onClick={() => remove(field.name)}
                                style={{ color: "#ff4d4f", fontSize: 18, cursor: "pointer" }}
                              />
                            )}
                            {isLast && fields.length < rollTypes.length && (
                              <ButtonComponent
                                type="primary"
                                text="Add Role"
                                icon={<PlusCircleOutlined />}
                                iconPosition="left"
                                onClick={() => add()}
                              />
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </>
                )}
              </Form.List>

              <Row gutter={24}>
                <Col span={24} style={{ textAlign: "right" }}>
                  <ButtonComponent
                    htmlType="submit"
                    text="Submit User"
                    type="primary"
                    className="mr10"
                    loading={loading}
                  />
                  {/* <Button type="primary" htmlType="submit">
                    Submit User
                  </Button> */}
                </Col>
              </Row>
            </Form>

            {/* </Modal> */}
          </CustomModal>

          {/* Edit Modal -- multi-role Banker + BankerRoles user */}
          <CustomModal
            visible={isEditBankerUserModalVisible}
            onClose={() => setIsEditBankerUserModalVisible(false)}
            onPrimaryAction={() => editBankerUserForm.submit()}
            onSecondaryAction={() => setIsEditBankerUserModalVisible(false)}
            title="Edit User"
            primaryButtonText="Update"
            secondaryButtonText="Cancel"
            showSecondaryButton
            className="edit-user-popoup"
            destroyOnClose
            primaryButtonProps={{
            }}
          >
            <Form form={editBankerUserForm} layout="vertical" onFinish={onFinishEditBankerUser} className="bank_admin_dashboard">
              <Row gutter={16}>
                <Col span={12}>
                  <FormItemInput
                    label="First Name"
                    name="firstName"
                    placeholder="Enter First Name"
                    disabled={editingBankerUser?.IsRegistered === true}
                    rules={[
                      { required: true, message: "First Name is required" },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <FormItemAlphabetInput
                    label="Middle Name"
                    name="middleName"
                    placeholder="Enter Middle Name"
                    disabled={editingBankerUser?.IsRegistered === true}
                    // rules={[
                    //   { required: false, max: 30, message: "Maximum 30 characters allowed" },
                    // ]}
                    maxLength={30}
                  />
                </Col>
                <Col span={12}>
                  <FormItemInput
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter Last Name"
                    disabled={editingBankerUser?.IsRegistered === true}
                    rules={[
                      { required: true, message: "Last Name is required" },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <FormItemInput
                    label="Email"
                    name="email"
                    placeholder="Enter Email"
                    disabled={editingBankerUser?.IsRegistered === true}
                    rules={[
                      { required: true, message: "Email is required" },
                      { type: "email", message: "Please enter a valid email" },
                    ]}
                  />
                </Col>
              </Row>

              <Form.List name="bankerRoles">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => {
                      const isLast = index === fields.length - 1;
                      const selectedRoleId = editBankerRolesWatch?.[index]?.role;
                      const selectedRoleName = rollTypes.find((obj) => obj.Id === selectedRoleId)?.Name;
                      const hideRec = !selectedRoleId || HIDE_RECOMMENDATION_ROLE_NAMES.includes(selectedRoleName);
                      const hideApp = !selectedRoleId || HIDE_APPROVAL_ROLE_NAMES.includes(selectedRoleName);
                      const showLimit = !hideRec || !hideApp;
                      // field.key is assigned per row when bankerRoles was populated from
                      // editingBankerUser.BankerRoles (0..n-1, in order) and stays stable across
                      // add/remove -- unlike editBankerRolesWatch, it's available immediately on
                      // the first render after opening the modal, with no watch-subscription lag.
                      const originalBankerRole = (editingBankerUser?.BankerRoles || [])[field.key];
                      // Roles already tied to a Credit Facility can't be removed from here.
                      const isDeleteRoleDisabled = originalBankerRole?.IsAssignInCreditfacilty === true;
                      // A role row that already exists in the saved BankerRoles (i.e. not one
                      // just added via "Add Role" in this session) is locked -- role + its
                      // amount fields stay disabled once saved.
                      const isSavedRole = !!originalBankerRole;
                      const takenElsewhere = (editBankerRolesWatch || [])
                        .filter((_, i) => i !== index)
                        .map((r) => r?.role);
                      const roleOptions = rollTypes
                        .filter((obj) => obj.Id === selectedRoleId || !takenElsewhere.includes(obj.Id))
                        .map((obj) => ({ value: obj.Id, label: obj.Name }));

                      return (
                        <Row gutter={16} key={field.key} align="top">
                          <Col span={showLimit ? 8 : 16}>
                            <FormItemSelect
                              label="Role"
                              name={[field.name, "role"]}
                              placeholder="Select role"
                              rules={[{ required: true, message: "Role is required" }]}
                              options={roleOptions}
                              disabled={isSavedRole}
                              //  disabled={editingBankerUser?.IsRegistered === true}
                            />
                          </Col>
                          {!hideRec && (
                            <Col span={8}>
                              <FormItemCurrencyInput
                                label="Recommendation Limit $"
                                name={[field.name, "recommendationLimit"]}
                                placeholder="Enter Recommendation Limit"
                                rules={[
                                  { required: true, message: "Recommendation Limit is required" },
                                ]}
                                // disabled={isSavedRole}
                                //  disabled={editingBankerUser?.IsRegistered === true}
                              />
                            </Col>
                          )}
                          {!hideApp && (
                            <Col span={8}>
                              <FormItemCurrencyInput
                                label="Approval Limit $"
                                name={[field.name, "approvalLimit"]}
                                placeholder="Enter Approval Limit"
                                rules={[
                                  { required: true, message: "Approval Limit is required" },
                                ]}
                                // disabled={isSavedRole}
                                // disabled={editingBankerUser?.IsRegistered === true}
                              />
                            </Col>
                          )}
                          <Col span={8} style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 33 }}>
                            {fields.length > 1 && (
                              <MinusCircleOutlined
                                className="removeRoleIcon"
                                onClick={() => {
                                  if (isDeleteRoleDisabled) return;
                                  if (selectedRoleId) {
                                    setRemovedEditBankerRoleIds((prev) => [...prev, selectedRoleId]);
                                  }
                                  remove(field.name);
                                }}
                                style={{
                                  color: "#ff4d4f",
                                  fontSize: 18,
                                  cursor: isDeleteRoleDisabled ? "not-allowed" : "pointer",
                                }}
                              />
                            )}
                            {isLast && fields.length < rollTypes.length && (
                              <ButtonComponent
                                type="primary"
                                text="Add Role"
                                icon={<PlusCircleOutlined />}
                                iconPosition="left"
                                onClick={() => add()}
                              />
                            )}
                          </Col>
                        </Row>
                      );
                    })}
                  </>
                )}
              </Form.List>
            </Form>
          </CustomModal>
        </div>
      </SidebarLayout>
    </Spin>
  );
};

export default BankAdminDashboard;
